import { useMemoizedFn } from "ahooks";
import { isNil } from "lodash";
import { useMemo } from "react";
import { AddonLineItem, CatalogLineItem, useCreatePurchaseStore } from "@/stores/createPurchaseStore";

/**
 * Updates one item's add-on line items. A package passes its item's own line items, since that item is not the
 * store's active one.
 */
export const useAddOnLineItemsForItem = (itemIndex: number, itemAddonLineItems?: AddonLineItem[]) => {
    const storeAddonLineItems = useCreatePurchaseStore((state) => state.purchase.items[itemIndex]?.addonLineItems);

    const overrideRestrictions = useMemo(() => {
        const addonLineItems = itemAddonLineItems ?? storeAddonLineItems ?? [];
        const overriddenEntries = addonLineItems
            .filter((lineItem) => lineItem.overrideRestrictions && lineItem.template?.id)
            .map((lineItem) => [lineItem.template.id as ID, true]);

        return Object.fromEntries(overriddenEntries) as Record<ID, boolean>;
    }, [itemAddonLineItems, storeAddonLineItems]);

    const getAddonLineItems = () => {
        const activeItemLineItems = useCreatePurchaseStore.getState().purchase.items[itemIndex]?.addonLineItems;
        return itemAddonLineItems ?? activeItemLineItems ?? [];
    };

    const setAddonLineItems = (addonLineItems: Array<AddonLineItem | CatalogLineItem>) => {
        useCreatePurchaseStore.getState().actions.setPurchase((draft) => {
            draft.items[itemIndex].addonLineItems = addonLineItems as AddonLineItem[];
        });
    };

    const handleOverrideChange = useMemoizedFn((value: boolean, templateId?: ID) => {
        const findLineItem = (lineItems?: AddonLineItem[]) => {
            return lineItems?.find((lineItem) => lineItem.template?.id === templateId);
        };
        const storeLineItems = useCreatePurchaseStore.getState().purchase.items[itemIndex]?.addonLineItems;
        const current = findLineItem(storeLineItems);
        if (!templateId || !current || current.overrideRestrictions === value) {
            return;
        }

        useCreatePurchaseStore.getState().actions.setPurchase((draft) => {
            const lineItem = findLineItem(draft.items[itemIndex]?.addonLineItems);
            if (lineItem) {
                lineItem.overrideRestrictions = value;
            }
        });
    });

    const handleQuantityChange = useMemoizedFn((quantity: number, templateId?: ID) => {
        const addonLineItems = getAddonLineItems();
        const previous = addonLineItems.find((lineItem) => lineItem.template?.id === templateId);
        const newLineItems: AddonLineItem[] = addonLineItems.filter((lineItem) => {
            return !isNil(lineItem.template) && lineItem.template.id !== templateId;
        });

        if (quantity > 0 && templateId) {
            newLineItems.push({
                quantity,
                template: { id: templateId },
                // Keep the override when the line item is rebuilt on a quantity change.
                ...(previous?.overrideRestrictions && { overrideRestrictions: true }),
            } as AddonLineItem);
        }

        setAddonLineItems(newLineItems);
    });

    const handleChoiceChange = useMemoizedFn((templateIds: ID[], selectedItemId: ID) => {
        const newLineItems: Array<AddonLineItem | CatalogLineItem> = getAddonLineItems().filter((lineItem) => {
            const lineItemTemplateId = lineItem.template?.id;
            return !isNil(lineItemTemplateId) && !templateIds.includes(lineItemTemplateId);
        });

        if (selectedItemId !== "None") {
            newLineItems.push({
                quantity: 1,
                template: { id: selectedItemId },
                catalog: { template: selectedItemId },
            } as CatalogLineItem);
        }

        setAddonLineItems(newLineItems);
    });

    return { overrideRestrictions, handleOverrideChange, handleQuantityChange, handleChoiceChange };
};
